<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import FeatureLockedScreen from 'dashboard/components/FeatureLockedScreen.vue';
import { productsApi } from 'dashboard/api/products';

const { t } = useI18n();
const { accountId } = useAccount();
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');

const hasFeature = computed(() =>
  isFeatureEnabled.value(accountId.value, 'products_catalog')
);

// ── State ──
const products = ref([]);
const loading = ref(false);
const search = ref('');
const showModal = ref(false);
const saving = ref(false);
const editingProduct = ref(null);
const confirmDelete = ref(null);

const CATEGORIES = [
  { value: 'pension', label: t('PRODUCTS.CATEGORIES.pension') },
  { value: 'risk', label: t('PRODUCTS.CATEGORIES.risk') },
  { value: 'contribution', label: t('PRODUCTS.CATEGORIES.contribution') },
  { value: 'general', label: t('PRODUCTS.CATEGORIES.general') },
];

const COMMISSION_TYPES = [
  { value: 'percentage', label: t('PRODUCTS.FORM.COMMISSION_TYPE_PERCENTAGE') },
  { value: 'fixed', label: t('PRODUCTS.FORM.COMMISSION_TYPE_FIXED') },
];

const emptyForm = () => ({
  code: '',
  name: '',
  description: '',
  category: 'general',
  color: '#3b82f6',
  active: true,
  position: 0,
  price: 0,
  commission_type: 'percentage',
  commission_value: 0,
});

const form = ref(emptyForm());

const filtered = computed(() => {
  const q = search.value.toLowerCase();
  if (!q) return products.value;
  return products.value.filter(
    p => p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q)
  );
});

// ── API ──
async function fetchProducts() {
  loading.value = true;
  try {
    const res = await productsApi.list();
    products.value = res.data?.payload || [];
  } catch {
    // silent
  } finally {
    loading.value = false;
  }
}

function openNew() {
  editingProduct.value = null;
  form.value = emptyForm();
  showModal.value = true;
}

function openEdit(product) {
  editingProduct.value = product;
  form.value = {
    code: product.code,
    name: product.name,
    description: product.description || '',
    category: product.category || 'general',
    color: product.color || '#3b82f6',
    active: product.active,
    position: product.position || 0,
    price: product.price || 0,
    commission_type: product.commission_type || 'percentage',
    commission_value: product.commission_value || 0,
  };
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
  editingProduct.value = null;
}

async function saveProduct() {
  saving.value = true;
  try {
    if (editingProduct.value) {
      const res = await productsApi.update(editingProduct.value.id, form.value);
      const idx = products.value.findIndex(
        p => p.id === editingProduct.value.id
      );
      if (idx !== -1) products.value[idx] = res.data;
    } else {
      const res = await productsApi.create(form.value);
      products.value.push(res.data);
    }
    closeModal();
  } catch {
    // silent
  } finally {
    saving.value = false;
  }
}

async function toggleActive(product) {
  try {
    const res = await productsApi.update(product.id, {
      active: !product.active,
    });
    const idx = products.value.findIndex(p => p.id === product.id);
    if (idx !== -1) products.value[idx] = res.data;
  } catch {
    // silent
  }
}

function askDelete(product) {
  confirmDelete.value = product;
}

async function doDelete() {
  if (!confirmDelete.value) return;
  try {
    await productsApi.delete(confirmDelete.value.id);
    products.value = products.value.filter(
      p => p.id !== confirmDelete.value.id
    );
  } catch {
    // silent
  } finally {
    confirmDelete.value = null;
  }
}

function categoryLabel(cat) {
  return CATEGORIES.find(c => c.value === cat)?.label || cat;
}

onMounted(fetchProducts);
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden">
    <FeatureLockedScreen v-if="!hasFeature" feature-name="products_catalog" />

    <template v-else>
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
      >
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('PRODUCTS.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-11 mt-0.5">
            {{ t('PRODUCTS.SUBTITLE') }}
          </p>
        </div>
        <button
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:bg-n-brand/90 transition-colors"
          @click="openNew"
        >
          <i class="i-lucide-plus size-4" />
          {{ t('PRODUCTS.ADD_BUTTON') }}
        </button>
      </div>

      <!-- Search -->
      <div class="px-6 py-3 border-b border-n-weak">
        <div class="relative max-w-xs">
          <i
            class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-10"
          />
          <input
            v-model="search"
            type="text"
            :placeholder="t('PRODUCTS.SEARCH_PLACEHOLDER')"
            class="w-full pl-9 pr-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-brand"
          />
        </div>
      </div>

      <!-- Table -->
      <div class="flex-1 overflow-auto px-6 py-4">
        <div v-if="loading" class="flex items-center justify-center h-40">
          <i class="i-lucide-loader-2 size-6 text-n-slate-10 animate-spin" />
        </div>

        <div
          v-else-if="filtered.length === 0"
          class="flex flex-col items-center justify-center h-40 text-n-slate-10"
        >
          <i class="i-lucide-package size-10 mb-2" />
          <span class="text-sm">{{ t('PRODUCTS.EMPTY_STATE') }}</span>
        </div>

        <table v-else class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-weak text-left text-n-slate-10">
              <th class="pb-3 pr-4 font-medium">
                {{ t('PRODUCTS.TABLE.CODE') }}
              </th>
              <th class="pb-3 pr-4 font-medium">
                {{ t('PRODUCTS.TABLE.NAME') }}
              </th>
              <th class="pb-3 pr-4 font-medium">
                {{ t('PRODUCTS.TABLE.CATEGORY') }}
              </th>
              <th class="pb-3 pr-4 font-medium">
                {{ t('PRODUCTS.TABLE.PRICE') }}
              </th>
              <th class="pb-3 pr-4 font-medium">
                {{ t('PRODUCTS.TABLE.COMMISSION') }}
              </th>
              <th class="pb-3 pr-4 font-medium">
                {{ t('PRODUCTS.TABLE.COLOR') }}
              </th>
              <th class="pb-3 pr-4 font-medium">
                {{ t('PRODUCTS.TABLE.ACTIVE') }}
              </th>
              <th class="pb-3 font-medium text-right">
                {{ t('PRODUCTS.TABLE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="product in filtered"
              :key="product.id"
              class="border-b border-n-weak/50 hover:bg-n-alpha-1 transition-colors"
            >
              <td class="py-3 pr-4">
                <span class="font-mono font-semibold text-n-slate-12">{{
                  product.code
                }}</span>
              </td>
              <td class="py-3 pr-4 text-n-slate-12">{{ product.name }}</td>
              <td class="py-3 pr-4">
                <span
                  class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
                >
                  {{ categoryLabel(product.category) }}
                </span>
              </td>
              <td class="py-3 pr-4 text-n-slate-12 font-mono text-xs">
                {{
                  product.price ? `R$ ${Number(product.price).toFixed(2)}` : '-'
                }}
              </td>
              <td class="py-3 pr-4 text-n-slate-11 text-xs">
                <template v-if="product.commission_value > 0">
                  {{
                    product.commission_type === 'percentage'
                      ? `${Number(product.commission_value).toFixed(1)}%`
                      : `R$ ${Number(product.commission_value).toFixed(2)}`
                  }}
                </template>
                <template v-else>-</template>
              </td>
              <td class="py-3 pr-4">
                <div class="flex items-center gap-2">
                  <div
                    class="size-5 rounded-full border border-n-weak/50"
                    :style="{ backgroundColor: product.color }"
                  />
                  <span class="text-xs text-n-slate-10 font-mono">{{
                    product.color
                  }}</span>
                </div>
              </td>
              <td class="py-3 pr-4">
                <button
                  class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none"
                  :class="product.active ? 'bg-n-brand' : 'bg-n-slate-6'"
                  @click="toggleActive(product)"
                >
                  <span
                    class="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
                    :class="
                      product.active ? 'translate-x-4' : 'translate-x-0.5'
                    "
                  />
                </button>
              </td>
              <td class="py-3 text-right">
                <div class="flex items-center justify-end gap-2">
                  <button
                    class="p-1.5 rounded hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-colors"
                    @click="openEdit(product)"
                  >
                    <i class="i-lucide-pencil size-4" />
                  </button>
                  <button
                    class="p-1.5 rounded hover:bg-n-alpha-2 text-n-slate-10 hover:text-ruby-9 transition-colors"
                    @click="askDelete(product)"
                  >
                    <i class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Create/Edit Modal -->
      <div
        v-if="showModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        @click.self="closeModal"
      >
        <div
          class="bg-n-background rounded-xl shadow-xl w-full max-w-md mx-4 p-6"
        >
          <h2 class="text-lg font-semibold text-n-slate-12 mb-4">
            {{
              editingProduct
                ? t('PRODUCTS.FORM.TITLE_EDIT')
                : t('PRODUCTS.FORM.TITLE_NEW')
            }}
          </h2>

          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('PRODUCTS.FORM.CODE') }}
                </label>
                <input
                  v-model="form.code"
                  type="text"
                  :placeholder="t('PRODUCTS.FORM.CODE_PLACEHOLDER')"
                  class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('PRODUCTS.FORM.CATEGORY') }}
                </label>
                <select
                  v-model="form.category"
                  class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                >
                  <option
                    v-for="cat in CATEGORIES"
                    :key="cat.value"
                    :value="cat.value"
                  >
                    {{ cat.label }}
                  </option>
                </select>
              </div>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('PRODUCTS.FORM.NAME') }}
              </label>
              <input
                v-model="form.name"
                type="text"
                :placeholder="t('PRODUCTS.FORM.NAME_PLACEHOLDER')"
                class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('PRODUCTS.FORM.DESCRIPTION') }}
              </label>
              <textarea
                v-model="form.description"
                rows="2"
                :placeholder="t('PRODUCTS.FORM.DESCRIPTION_PLACEHOLDER')"
                class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand resize-none"
              />
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('PRODUCTS.FORM.PRICE') }}
              </label>
              <input
                v-model.number="form.price"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('PRODUCTS.FORM.COMMISSION_TYPE') }}
                </label>
                <select
                  v-model="form.commission_type"
                  class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                >
                  <option
                    v-for="ct in COMMISSION_TYPES"
                    :key="ct.value"
                    :value="ct.value"
                  >
                    {{ ct.label }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('PRODUCTS.FORM.COMMISSION_VALUE') }}
                </label>
                <input
                  v-model.number="form.commission_value"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
            </div>

            <div class="flex items-center gap-4">
              <div class="flex-1">
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('PRODUCTS.FORM.COLOR') }}
                </label>
                <div class="flex items-center gap-2">
                  <input
                    v-model="form.color"
                    type="color"
                    class="h-9 w-12 rounded border border-n-weak cursor-pointer"
                  />
                  <input
                    v-model="form.color"
                    type="text"
                    class="flex-1 px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 font-mono focus:outline-none focus:ring-1 focus:ring-n-brand"
                  />
                </div>
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('PRODUCTS.FORM.ACTIVE') }}
                </label>
                <button
                  class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
                  :class="form.active ? 'bg-n-brand' : 'bg-n-slate-6'"
                  type="button"
                  @click="form.active = !form.active"
                >
                  <span
                    class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                    :class="form.active ? 'translate-x-5' : 'translate-x-0.5'"
                  />
                </button>
              </div>
            </div>
          </div>

          <div class="flex justify-end gap-3 mt-6">
            <button
              class="px-4 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
              @click="closeModal"
            >
              {{ t('PRODUCTS.FORM.CANCEL') }}
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:bg-n-brand/90 transition-colors disabled:opacity-50"
              :disabled="saving || !form.name || !form.code"
              @click="saveProduct"
            >
              <i
                v-if="saving"
                class="i-lucide-loader-2 size-4 animate-spin mr-1"
              />
              {{ t('PRODUCTS.FORM.SAVE') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Confirm Modal -->
      <div
        v-if="confirmDelete"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        @click.self="confirmDelete = null"
      >
        <div
          class="bg-n-background rounded-xl shadow-xl w-full max-w-sm mx-4 p-6"
        >
          <h2 class="text-lg font-semibold text-n-slate-12 mb-2">
            {{ t('PRODUCTS.CONFIRM_DELETE.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11 mb-6">
            {{
              t('PRODUCTS.CONFIRM_DELETE.MESSAGE', { name: confirmDelete.name })
            }}
          </p>
          <div class="flex justify-end gap-3">
            <button
              class="px-4 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
              @click="confirmDelete = null"
            >
              {{ t('PRODUCTS.CONFIRM_DELETE.CANCEL') }}
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-ruby-9 rounded-lg hover:bg-ruby-10 transition-colors"
              @click="doDelete"
            >
              {{ t('PRODUCTS.CONFIRM_DELETE.CONFIRM') }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
